import { cn } from "../../../utils/cn.js";
import * as React from "react";
import { jsx } from "react/jsx-runtime";
import { ScrollArea as ScrollArea$1 } from "@base-ui/react/scroll-area";
//#region src/components/ScrollArea/ScrollArea.tsx
/**
* Groups all parts of the scroll area. Delegates to the Base UI
* ScrollArea.Root primitive and renders a `<div>`.
*
* @param props - Root container props forwarded to Base UI ScrollArea.Root
* @returns The rendered scroll area root
*/
function ScrollArea({ className, ...props }) {
	return /* @__PURE__ */ jsx(ScrollArea$1.Root, {
		className: cn("relative", className),
		...props
	});
}
/**
* The actual scrollable container of the scroll area. Renders a `<div>`
* that handles overflow and houses the content.
*
* When `scrollFade` is enabled, sticky pseudo-element gradients are rendered
* at the top and bottom edges, driven by Base UI's overflow CSS variables.
* Negative margins cancel out the layout space the pseudo-elements consume.
*
* Fades also stick on the inline-start axis (`start-0`) so a viewport-wide
* strip stays pinned across the full visible scrollport during horizontal
* scroll. Without that, the strip stays at content `x = 0` and only covers
* the first visible slice of a wide pane (e.g. DataTable).
*
* Control the gradient with two CSS custom properties on this element or a
* parent:
*
* - `--scroll-fade-color` — gradient start colour (default `white`)
* - `--scroll-fade-size` — maximum gradient height (default `40px`)
*
* @param props - Viewport props forwarded to Base UI ScrollArea.Viewport
* @returns The rendered scrollable viewport
*/
function ScrollAreaViewport({ className, chainVerticalScroll = false, scrollFade = false, children, ref, ...props }) {
	const viewportRef = React.useRef(null);
	const setViewportRef = React.useCallback((node) => {
		viewportRef.current = node;
		if (typeof ref === "function") ref(node);
		else if (ref) ref.current = node;
	}, [ref]);
	return /* @__PURE__ */ jsx(ScrollArea$1.Viewport, {
		ref: setViewportRef,
		className: cn(chainVerticalScroll ? "overscroll-y-auto overscroll-x-contain" : "overscroll-contain", scrollFade && [
			"before:content-[''] after:content-['']",
			"before:pointer-events-none after:pointer-events-none",
			"before:sticky after:sticky",
			"before:top-0 after:bottom-0",
			"before:start-0 after:start-0",
			"before:z-[1] after:z-[1]",
			"before:block after:block",
			"before:w-full after:w-full",
			"before:[--scroll-area-overflow-y-start:inherit]",
			"after:[--scroll-area-overflow-y-end:inherit]",
			"before:[height:min(var(--scroll-fade-size,40px),var(--scroll-area-overflow-y-start,0px))]",
			"after:[height:min(var(--scroll-fade-size,40px),var(--scroll-area-overflow-y-end,0px))]",
			"before:[margin-bottom:calc(-1*min(var(--scroll-fade-size,40px),var(--scroll-area-overflow-y-start,0px)))]",
			"after:[margin-top:calc(-1*min(var(--scroll-fade-size,40px),var(--scroll-area-overflow-y-end,0px)))]",
			"before:bg-gradient-to-b before:from-[var(--scroll-fade-color,white)] before:to-transparent",
			"after:bg-gradient-to-t after:from-[var(--scroll-fade-color,white)] after:to-transparent"
		], className),
		...props,
		children
	});
}
/**
* Container for the content inside the scroll area viewport. Wraps children
* so that Base UI can measure overflow dimensions.
*
* @param props - Content wrapper props forwarded to Base UI ScrollArea.Content
* @returns The rendered content wrapper
*/
function ScrollAreaContent(props) {
	return /* @__PURE__ */ jsx(ScrollArea$1.Content, { ...props });
}
/**
* A vertical or horizontal scrollbar track. Fades in when the user hovers
* over or scrolls the area, and fades out when idle.
*
* @param props - Scrollbar props including orientation
* @param props.orientation - `"vertical"` (default) or `"horizontal"`
* @returns The rendered scrollbar track
*/
function ScrollAreaScrollbar({ className, orientation = "vertical", ...props }) {
	return /* @__PURE__ */ jsx(ScrollArea$1.Scrollbar, {
		orientation,
		className: cn("flex touch-none select-none", "opacity-0 transition-opacity duration-150", "data-[hovering]:opacity-100 data-[scrolling]:opacity-100", orientation === "vertical" && "mr-px h-full w-4 flex-col py-px [&>*]:ml-auto [&>*]:w-1.5 [&>*]:rounded-full [&>*]:transition-[width] [&>*]:duration-150 hover:[&>*]:w-2.5", orientation === "horizontal" && "mb-px h-4 w-full flex-row px-px [&>*]:mt-auto [&>*]:h-1.5 [&>*]:rounded-full [&>*]:transition-[height] [&>*]:duration-150 hover:[&>*]:h-2.5", className),
		...props
	});
}
/**
* The draggable thumb inside the scrollbar that indicates the current
* scroll position. Styled with a rounded neutral background.
*
* @param props - Thumb props forwarded to Base UI ScrollArea.Thumb
* @returns The rendered scrollbar thumb
*/
function ScrollAreaThumb({ className, ...props }) {
	return /* @__PURE__ */ jsx(ScrollArea$1.Thumb, {
		className: cn("bg-background-accent-cement-subtle-hover", className),
		...props
	});
}
/**
* Small rectangular area at the intersection of horizontal and vertical
* scrollbars. Only renders when both scrollbars are present.
*
* @param props - Corner props forwarded to Base UI ScrollArea.Corner
* @returns The rendered scrollbar corner
*/
function ScrollAreaCorner(props) {
	return /* @__PURE__ */ jsx(ScrollArea$1.Corner, { ...props });
}
//#endregion
export { ScrollArea, ScrollAreaContent, ScrollAreaCorner, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport };

//# sourceMappingURL=ScrollArea.js.map